import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicConfig, SessionResponse, SessionUser } from '@shared/types';
import { api } from './api';

export const sessionKey = ['session'] as const;

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: () => api<SessionResponse>('/session'),
    staleTime: 60_000,
  });
}

export function useUser(): SessionUser | null {
  return useSession().data?.user ?? null;
}

const FALLBACK: PublicConfig = {
  serverName: 'Project R',
  tagline: 'Classic MMORPG adventure, anywhere you play',
  turnstileSiteKey: null,
  discordInvite: null,
  discordGuildId: null,
  facebookUrl: null,
  iosTestflightUrl: null,
  showGameIcons: true,
  donationsEnabled: false,
  preview: false,
  previewAllowed: true,
  banner: null,
  rateLabel: 'Low rate',
  testPhase: 'Alpha test',
  preregOpen: false,
  rates: { base: 150, job: 150, drop: 150 },
  ratesNote: '',
  pointsPerUsd: 55,
  afkFreeSeconds: 3600,
  afkVipSeconds: 14400,
  partners: {
    suggestedPrice: 'US$1 = 55 points',
    reseller: {
      minTopupUsdt: 100,
      tiers: [
        { minUsdt: 100, discountPct: 15 },
        { minUsdt: 200, discountPct: 18 },
        { minUsdt: 500, discountPct: 20 },
      ],
      minSend: 100,
      dailySendCap: 200_000,
      stepUpAbove: 5000,
      requestTtlHours: 24,
      maxOpenRequests: 3,
      requestMax: 50_000,
    },
    creator: { supportPct: 10, supportExpiryDays: 30, supportMinGift: 10, cookieDays: 30, cashoutMinPoints: 500, cashoutPhpPerPoint: 1 },
    bonusTiers: [
      { minUsdt: 20, bonusPct: 3 },
      { minUsdt: 50, bonusPct: 5 },
      { minUsdt: 100, bonusPct: 8 },
    ],
  },
  showZenyRanking: false,
  discordLogin: false,
  discordRoles: false,
  devNo2fa: false,
};

export function useConfig(): PublicConfig {
  return useSession().data?.config ?? FALLBACK;
}

/** After login, logout or an account change: refetch everything user-shaped. */
export function useRefreshSession() {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: sessionKey });
    await qc.invalidateQueries({ queryKey: ['account'] });
    await qc.invalidateQueries({ queryKey: ['partner'] });
  };
}
